import React, { useState, useEffect, useContext, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import { Collapse, Select, Modal, Slider, InputNumber, Row, Col } from 'antd';

import HighchartsExporting from 'highcharts/modules/exporting';

if (typeof Highcharts === 'object') {
    HighchartsExporting(Highcharts);
}

const TimeplotChart = (props) => {
    const { Panel } = Collapse;

    const [isExportModalVisible, setIsExportModalVisible] = useState(false);
    const [selectedInterval, setSelectedInterval] = useState(1);

    const internalChartRef = useRef(null);
    const activeRef = props.forwardedRef || internalChartRef;



    let timeplotData = props.getAnalysisRes?.timeplot;
    let timeplotDisplayName = props.getAnalysisRes?.display_name;

    // Check x-axis type from analysis data (user selection) - stored in variables JSONField
    const xAxisType = props.analysisData?.variables?.timeplot_xaxis_type?.type || 'index';
    const hasMonthColumn = props.getAnalysisRes?.has_month_column || (xAxisType === 'year');

    const maxLimit = hasMonthColumn ? 20 : 1000;

    useEffect(() => {
        if (selectedInterval > maxLimit) {
            setSelectedInterval(maxLimit);
        }
    }, [hasMonthColumn, maxLimit, selectedInterval, setSelectedInterval]);

    // Helper function to detect data format from original data array
    const detectDataFormat = (dataArray) => {
        if (!dataArray || !Array.isArray(dataArray) || dataArray.length === 0) {
            return 'monthly'; // default
        }

        // Check first few items to determine format
        for (let i = 0; i < Math.min(5, dataArray.length); i++) {
            const item = dataArray[i];
            let monthStr = null;

            if (Array.isArray(item)) {
                monthStr = item[0];
            } else if (typeof item === 'object' && item !== null) {
                monthStr = item.month;
            }

            if (monthStr && typeof monthStr === 'string') {
                // Check for quarterly format: YYYY-Q1, YYYY-Q2, etc.
                if (monthStr.match(/^\d{4}-Q\d{1}$/)) {
                    return 'quarterly';
                }
                // Check for yearly format: YYYY
                if (monthStr.match(/^\d{4}$/)) {
                    return 'yearly';
                }
                // Check for monthly format: YYYY-MM
                if (monthStr.match(/^\d{4}-\d{2}$/)) {
                    return 'monthly';
                }
            }
        }

        return 'monthly'; // default
    };

    // const handleExport = (type) => {

    //     console.log('Exporting chart as:', type, 'with interval:', selectedInterval);
    //     const chart = activeRef.current?.chart; 
    //     if (!chart) return;

    //     chart.exportChart(
    //         {
    //             type: type,
    //             filename: 'timeplot-chart'
    //         },
    //         {
    //             xAxis: [{
    //                 tickInterval: hasMonthColumn 
    //                     ? selectedInterval * 365.24 * 24 * 3600 * 1000 
    //                     : selectedInterval,
    //                 labels: {
    //                     enabled: true,
    //                     rotation: -45,
    //                     step: 1, 
    //                     style: { fontSize: '8px' }
    //                 }
    //             }]
    //         }
    //     );
    //     setIsExportModalVisible(false);
    // };

    const handleExport = (type) => {
        const chart = activeRef.current?.chart;
        if (!chart) return;

        let exportXAxisOptions = {};

        if (hasMonthColumn) {
            const { min, max } = chart.xAxis[0].getExtremes();
            const tickPositions = [];

            let current = new Date(min);
            current.setUTCMonth(0, 1, 0, 0);
            let currentTimestamp = current.getTime();

            while (currentTimestamp <= max) {
                tickPositions.push(currentTimestamp);
                current.setUTCFullYear(current.getUTCFullYear() + selectedInterval);
                currentTimestamp = current.getTime();
            }

            exportXAxisOptions = {
                tickPositions: tickPositions,
                labels: {
                    enabled: true,
                    rotation: -45,
                    format: '{value:%Y}',
                    style: { fontSize: '10px' }
                }
            };
        } else {
            exportXAxisOptions = {
                tickInterval: selectedInterval,
                labels: {
                    enabled: true,
                    rotation: -45,
                    style: { fontSize: '10px' }
                }
            };
        }

        chart.exportChart(
            {
                type: type,
                filename: 'timeplot-chart'
            },
            {
                xAxis: [exportXAxisOptions]
            }
        );
        setIsExportModalVisible(false);
    };

    // Helper function to transform data for a single variable
    const transformVariableData = (dataArray, useIndex = false) => {
        if (!dataArray || !Array.isArray(dataArray)) {
            return [];
        }

        return dataArray.map((item, arrayIndex) => {
            let monthValue, indexValue, yValue;

            // Handle array format: [index, value] or [month, value]
            if (Array.isArray(item)) {
                // Check if first element looks like a month string (YYYY-MM format)
                if (item[0] && typeof item[0] === 'string' && item[0].match(/^\d{4}-\d{2}$/)) {
                    monthValue = item[0];
                    yValue = item[1];
                } else if (typeof item[0] === 'number') {
                    // Format is [index, value] - same as original univariate timeplot
                    indexValue = item[0];
                    yValue = item[1];
                } else {
                    // Fallback: assume format is [index, value]
                    indexValue = item[0];
                    yValue = item[1];
                }
            }
            // Handle object format: {month: '2023-11', value: ...} or {index: 1, value: ...}
            else if (typeof item === 'object' && item !== null) {
                monthValue = item.month;
                indexValue = item.index;
                // Try different possible value property names
                yValue = item.value !== undefined ? item.value :
                    item.y !== undefined ? item.y :
                        item[Object.keys(item).find(key => key !== 'month' && key !== 'index')];
            }
            // Handle string format (just the month value)
            else if (typeof item === 'string' && item.match(/^\d{4}-\d{2}$/)) {
                monthValue = item;
                yValue = arrayIndex; // Fallback to index if no value provided
            }
            else {
                // Fallback: use item as value, no month
                monthValue = null;
                yValue = item;
            }

            // If using index or month is not available, use index
            if (useIndex || (!monthValue && indexValue !== undefined)) {
                const xValue = indexValue !== undefined ? indexValue : (arrayIndex + 1);
                if (yValue !== null && yValue !== undefined) {
                    return [xValue, parseFloat(yValue)];
                }
                return null;
            }

            // Extract year and month from month string
            // Formats: '2023-11' (monthly), '2023' (yearly), '2023-Q1' (quarterly)
            let year = null;
            let month = null;
            if (monthValue) {
                const monthStr = monthValue.toString();

                // Try monthly format: YYYY-MM
                const monthMatch = monthStr.match(/^(\d{4})-(\d{1,2})$/);
                if (monthMatch) {
                    year = parseInt(monthMatch[1]);
                    month = parseInt(monthMatch[2]);
                } else {
                    // Try quarterly format: YYYY-Q1, YYYY-Q2, etc.
                    const quarterMatch = monthStr.match(/^(\d{4})-Q(\d{1})$/);
                    if (quarterMatch) {
                        year = parseInt(quarterMatch[1]);
                        const quarter = parseInt(quarterMatch[2]);
                        // Convert quarter to month (Q1=Jan, Q2=Apr, Q3=Jul, Q4=Oct)
                        month = (quarter - 1) * 3 + 1;
                    } else {
                        // Try yearly format: YYYY
                        const yearMatch = monthStr.match(/^(\d{4})$/);
                        if (yearMatch) {
                            year = parseInt(yearMatch[1]);
                            month = 1; // Default to January for yearly data
                        }
                    }
                }
            }

            // Return [timestamp, value] for Highcharts datetime axis
            // Use the 1st day of the actual month as the timestamp
            if (year !== null && month !== null && yValue !== null && yValue !== undefined) {
                // month-1 because Date.UTC uses 0-based months (0=January, 11=December)
                const timestamp = Date.UTC(year, month - 1, 1);
                const point = [timestamp, parseFloat(yValue)];
                return point;
            }

            return null;
        }).filter(item => item !== null);
    };

    // Determine if we have the new format (object with variables) or old format (array)
    let seriesData = [];
    let errorMessages = [];
    let detectedDataFormat = 'monthly'; // Default format

    if (timeplotData && typeof timeplotData === 'object' && !Array.isArray(timeplotData) && !timeplotData.message && !timeplotData.error) {
        // New format: object with Y, X1, X2, etc. keys (works for both univariate and multivariate)
        // Sort variables: Y first, then X1, X2, etc.
        const variableKeys = Object.keys(timeplotData).sort((a, b) => {
            if (a === 'Y') return -1;
            if (b === 'Y') return 1;
            return a.localeCompare(b);
        });

        variableKeys.forEach((varKey) => {
            const varInfo = timeplotData[varKey];
            if (varInfo && typeof varInfo === 'object') {
                // Check for error message first
                if (varInfo.error) {
                    console.log('Found error for variable:', varKey, varInfo.error);
                    errorMessages.push({
                        variable: varInfo.display_name || varKey,
                        message: varInfo.error
                    });
                }
                // Process data if available (even if there's an error, we might have partial data)
                if (varInfo.data && Array.isArray(varInfo.data) && varInfo.data.length > 0) {
                    // Detect format from first variable's data
                    if (detectedDataFormat === 'monthly' && varKey === 'Y') {
                        detectedDataFormat = detectDataFormat(varInfo.data);
                    }
                    const transformedData = transformVariableData(varInfo.data, !hasMonthColumn);
                    if (transformedData.length > 0) {
                        seriesData.push({
                            name: varInfo.display_name || varKey,
                            data: transformedData
                        });
                    }
                }
            }
        });
    } else if (timeplotData && Array.isArray(timeplotData)) {
        // Old format: array (backward compatibility)
        detectedDataFormat = detectDataFormat(timeplotData);
        const transformedData = transformVariableData(timeplotData, !hasMonthColumn);
        if (transformedData.length > 0) {
            seriesData.push({
                name: timeplotDisplayName || 'Variable',
                data: transformedData
            });
        }
    }

    // Color scheme matching other charts in the application
    const chartColors = ['#407cb1', '#60091d', '#76ac54', '#cb2e26', '#6D869F'];

    // Determine if this is multivariate (multiple series) or univariate (single series)
    const isMultivariate = seriesData.length > 1;

    let timeplotOptions = {
        credits: {
            enabled: false
        },
        chart: {
            zoomType: 'x'
        },
        boost: {
            useGPUTranslations: true,
            usePreAllocated: true
        },
        title: {
            text: ''
        },
        xAxis: hasMonthColumn ? {
            type: 'datetime',
            title: {
                text: 'Year'
            },
            dateTimeLabelFormats: {
                year: '%Y',
                // month: '%Y',
                // day: '%Y'
            },
            labels: {
                formatter: function () {
                    const year = new Date(this.value).getUTCFullYear();
                    // Get all tick positions and find the previous one
                    const axis = this.axis;
                    const tickPositions = axis.tickPositions;
                    const currentIndex = tickPositions.indexOf(this.value);

                    if (currentIndex === 0) {
                        // First tick, always show
                        return year.toString();
                    }

                    // Check the previous tick's year
                    const prevValue = tickPositions[currentIndex - 1];
                    const prevYear = new Date(prevValue).getUTCFullYear();

                    // Only show if this is a different year than the previous one
                    return year !== prevYear ? year.toString() : '';
                }
            },
            minTickInterval: 365 * 24 * 3600 * 1000
        } : {
            type: 'linear',
            title: {
                text: 'Index'
            }
        },
        yAxis: {
            title: {
                text: isMultivariate ? 'Value' : (timeplotDisplayName || 'Value')
            }
        },
        legend: {
            enabled: isMultivariate,
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal'
        },
        tooltip: {
            formatter: function () {
                // Helper function to round to 3 decimal places and format
                const roundTo3Decimals = (value) => {
                    if (value === null || value === undefined || isNaN(value)) {
                        return value;
                    }
                    return value.toFixed(3);
                };

                if (!hasMonthColumn) {
                    // For index-based charts, use default formatting
                    if (isMultivariate && this.points && this.points.length > 0) {
                        let tooltipText = `<b>Index: ${this.x}</b><br/>`;
                        this.points.forEach(function (point) {
                            tooltipText += `• ${point.series.name}: ${roundTo3Decimals(point.y)}<br/>`;
                        });
                        return tooltipText;
                    } else {
                        return `<b>Index: ${this.x}</b><br/>• ${this.series.name}: ${roundTo3Decimals(this.y)}`;
                    }
                }

                const date = new Date(this.x);
                const year = date.getUTCFullYear();
                const month = date.getUTCMonth(); // 0-based (0=Jan, 11=Dec)

                // Use detected format to determine label
                let dateLabel;

                if (detectedDataFormat === 'yearly') {
                    dateLabel = year.toString();
                } else if (detectedDataFormat === 'quarterly') {
                    // Calculate quarter from month: 0-2=Q1, 3-5=Q2, 6-8=Q3, 9-11=Q4
                    const quarter = Math.floor(month / 3) + 1;
                    dateLabel = `Q${quarter} ${year}`;
                } else {
                    // Monthly: use month name
                    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
                    dateLabel = `${monthNames[month]} ${year}`;
                }

                // Handle both shared tooltip (multiple series) and single point
                if (isMultivariate && this.points && this.points.length > 0) {
                    let tooltipText = `<b>${dateLabel}</b><br/>`;
                    this.points.forEach(function (point) {
                        tooltipText += `• ${point.series.name}: ${roundTo3Decimals(point.y)}<br/>`;
                    });
                    return tooltipText;
                } else {
                    // Single point tooltip
                    return `<b>${dateLabel}</b><br/>• ${this.series.name}: ${roundTo3Decimals(this.y)}`;
                }
            },
            shared: isMultivariate
        },
        plotOptions: {
            areaspline: {
                marker: {
                    enabled: false
                },
                lineWidth: 1,
                states: {
                    hover: {
                        lineWidth: 1
                    }
                },
                threshold: null
            }
        },
        series: seriesData.map((series, index) => {
            const seriesColor = chartColors[index % chartColors.length];
            return {
                type: 'areaspline',
                name: series.name,
                data: series.data,
                boostThreshold: 2000,
                color: seriesColor,
                marker: {
                    enabled: false
                },
                fillColor: {
                    linearGradient: {
                        x1: 0,
                        y1: 0,
                        x2: 0,
                        y2: 1
                    },
                    stops: [
                        [
                            1,
                            Highcharts.color(seriesColor)
                                .setOpacity(0)
                                .get('rgba')
                        ]
                    ]
                }
            };
        }),
        exporting: {
            enabled: true,
            buttons: {
                contextButton: {
                    menuItems: [
                        {
                            text: 'Set your own interval for X-axis labels',
                            onclick: function () {
                                setIsExportModalVisible(true);
                            }
                        },
                        { separator: true },
                        'downloadPNG',
                        'downloadJPEG',
                        'downloadPDF',
                        'downloadSVG',
                        'printChart'
                    ]
                }
            }
        },
    };

    // Always show error messages if they exist, instead of chart
    if (errorMessages.length > 0) {
        // Get the first error message (all should be the same)
        const errorMessage = errorMessages[0]?.message || 'Data structure error';

        return (
            <>
                <Collapse bordered={false} defaultActiveKey={[1]}>
                    <Panel key={1} header={`Timeplot ${props.getAnalysisRes?.filter_by_variable == null ? '' : `[${props.getAnalysisRes.filter_by_variable}]`}`}>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '4px',
                            color: '#856404',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            minHeight: '100px'
                        }}>
                            {errorMessage}
                        </div>
                    </Panel>
                </Collapse>
            </>
        );
    }

    return (
        <>
            <Modal
                className="basic-modal-legal-notice"
                title=""
                visible={isExportModalVisible}
                footer={null}
                closable={false}
                bodyStyle={{ minHeight: 400 }}
                width={400}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        height: '350px'
                    }}
                >
                    <div style={{ padding: '8px 0 0 0', marginLeft: 6 }}>
                        <p className="header-title" style={{ marginBottom: '20px' }}>
                            Export Settings
                        </p>
                        <p style={{ fontFamily: 'Roboto Regular', fontSize: '16px', lineHeight: '22px', marginBottom: '10px' }}>
                            Label interval ({hasMonthColumn ? 'years' : 'steps'}):
                        </p>

                        <div style={{ marginTop: '20px' }}>
                            <Row align="middle">
                                <Col span={16}>
                                    <Slider
                                        min={1}
                                        max={maxLimit}
                                        onChange={(value) => setSelectedInterval(value)}
                                        value={typeof selectedInterval === 'number' ? selectedInterval : 1}
                                    />
                                </Col>
                                <Col span={8}>
                                    <InputNumber
                                        min={1}
                                        max={maxLimit}
                                        style={{ marginLeft: '16px' }}
                                        value={selectedInterval}
                                        onChange={(value) => setSelectedInterval(value)}
                                    />
                                </Col>
                            </Row>
                            <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>
                                Current: every {selectedInterval} {hasMonthColumn ? 'year(s)' : 'index(es)'}
                            </p>
                        </div>
                    </div>

                    <div style={{ textAlign: 'center', marginTop: 22, padding: '0 5px', marginBottom: '6px' }}>
                        <div
                            onClick={() => handleExport('image/png')}
                            style={{
                                background: '#407CB1',
                                height: '35px',
                                color: '#fff',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontFamily: 'Roboto Medium',
                                letterSpacing: '0.02em',
                                cursor: 'pointer',
                                borderRadius: '3px',
                                marginBottom: '10px'
                            }}
                        >
                            DOWNLOAD PNG
                        </div>

                        <div
                            onClick={() => handleExport('image/jpeg')}
                            style={{
                                background: '#407CB1',
                                height: '35px',
                                color: '#fff',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontFamily: 'Roboto Medium',
                                letterSpacing: '0.02em',
                                cursor: 'pointer',
                                borderRadius: '3px',
                                marginBottom: '10px'
                            }}
                        >
                            DOWNLOAD JPEG
                        </div>

                        <div
                            onClick={() => handleExport('application/pdf')}
                            style={{
                                background: '#407CB1',
                                height: '35px',
                                color: '#fff',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontFamily: 'Roboto Medium',
                                letterSpacing: '0.02em',
                                cursor: 'pointer',
                                borderRadius: '3px'
                            }}
                        >
                            DOWNLOAD PDF
                        </div>

                        <div
                            onClick={() => setIsExportModalVisible(false)}
                            style={{
                                width: '100%',
                                marginTop: 15,
                                color: '#d14840',
                                border: '1px solid #d14840',
                                height: '35px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '3px',
                                fontSize: '14px',
                                fontFamily: 'Roboto Medium',
                                cursor: 'pointer'
                            }}
                        >
                            CANCEL
                        </div>
                    </div>
                </div>
            </Modal>
            <Collapse bordered={false} defaultActiveKey={[1]}>
                <Panel key={1} header={`Timeplot ${props.getAnalysisRes?.filter_by_variable == null ? '' : `[${props.getAnalysisRes.filter_by_variable}]`}`}>
                    {seriesData.length > 0 ? (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={timeplotOptions}
                            ref={props.forwardedRef}
                        />
                    ) : (
                        <div style={{ padding: '20px', textAlign: 'center' }}>No data available for display</div>
                    )}
                </Panel>
            </Collapse>

        </>
    );
};

export default React.memo(TimeplotChart);
